import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useSelector } from 'react-redux'

import { SegmentedControl } from 'cng-common/components/segmentedControl'
import { Translation } from 'cng-common/features/translations'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { setCockpitRevenueAndSobView } from '../../../../model'
import { selectCockpitRevenueAndSobView } from '../../../../model/selectors'
import { AchievementScoreBadge } from '../../../components/achievementScoreBadge'
import { CockpitSegmentedControlAccent } from '../../../components/cockpitSegmentedControlAccent'
import { PlanQualityBadge } from '../../../components/planQualityBadge'
import { useRevenueAndSobViewScores } from '../../hooks/useRevenueAndSobViewScores'

import styles from './CockpitTableViewSwitch.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS

export type TCockpitTableView = 'revenueAndSob' | 'planQuality'

interface ICockpitTableViewSwitchProps {
  /** Scores the Plan Quality tab off the cockpit's own category table, as that mode shows it. */
  isManagerCockpitMode?: boolean
  /** `data-tour-id` for the switch, from the cockpit whose tour points at it. */
  tourId?: string
}

/** The expanded Financial & SOB tile's view switch, drawn in the tile header's control row. Each
 *  tab wears its own score; a tab with nothing scored yet stays bare. */
export const CockpitTableViewSwitch = ({
  isManagerCockpitMode = false,
  tourId,
}: ICockpitTableViewSwitchProps) => {
  const dispatch = useAppDispatch()
  const requestedView = useSelector(selectCockpitRevenueAndSobView)
  const selectedView: TCockpitTableView =
    requestedView === 'quality' ? 'planQuality' : 'revenueAndSob'
  const { achievementScore, planQualityScore } = useRevenueAndSobViewScores(isManagerCockpitMode)

  const handleSelectView = (view: TCockpitTableView) => {
    dispatch(setCockpitRevenueAndSobView(view === 'planQuality' ? 'quality' : 'detail'))
  }

  return (
    <div data-tour-id={tourId}>
      <CockpitSegmentedControlAccent inHeader>
        <SegmentedControl
          useItemKeys
          selectedItem={selectedView}
          onItemSelect={handleSelectView}
          size="small"
          variant="veneer"
        >
          <SegmentedControl.Item itemKey="revenueAndSob">
            <span className={styles.itemLabel}>
              <Translation text={KEYS.revenueAndSob.tileTitle} span />
              {achievementScore !== undefined && <AchievementScoreBadge score={achievementScore} />}
            </span>
          </SegmentedControl.Item>
          <SegmentedControl.Item itemKey="planQuality">
            <span className={styles.itemLabel}>
              <Translation text={KEYS.planQuality.title} span />
              {planQualityScore !== undefined && <PlanQualityBadge score={planQualityScore} pill />}
            </span>
          </SegmentedControl.Item>
        </SegmentedControl>
      </CockpitSegmentedControlAccent>
    </div>
  )
}

CockpitTableViewSwitch.displayName = 'CockpitTableViewSwitch'
